<template>
  <div>
    <div class="section">
      <div v-if="isDataUpdating"
           class="page-cover-loader">
        <base-loader />
      </div>
      <div class="container" v-if="snapshotsMissing">
        <div class="rank-zone__no-data">Looks like you don’t have any ranking data to report.</div>
      </div>
      <div class="container" v-else-if="!isDataFetched">
        <div class="rank-zone__loader">
          <base-loader />
        </div>
      </div>
      <div class="container" v-else>
        <div class="section__row">
          <base-content-block class="section__row-item"
                              title="Sections">
            <div v-if="!isMonthlySearchVolume && !isDataUpdating"
                 class="no-data-block">
              Not enough data for drawing a chart
            </div>
            <div v-else
                 class="section__chart-wrapper">
              <PieChart :data="monthlySearchVolume.items"
                        :footerItem="monthlySearchVolume.footer" class="charts__pie"/>
            </div>
          </base-content-block>
          <base-content-block class="section__row-item"
                              title="Share of voice">
            <div v-if="!shareOfVoice.length && !isDataUpdating"
                 class="no-data-block">
              Not enough data for drawing a chart
            </div>
            <div v-else
                 class="section__chart-wrapper">
              <BarChart :list1="shareOfVoice" :list2="[]" :date1="snapshot_date" :date2="''" class="charts__pie"/>
            </div>
          </base-content-block>
        </div>

        <div class="table-wrapper">
          <div class="table-wrapper__header section__table-controls">
            <base-select class="section__select"
                         :showTopBorder="true"
                         v-model="snapshot_date"
                         :options="formattedSnapshots"
                         @change="onSnapshotChanged()"/>
          </div>
          <base-table-collapsible class="section__table"
                                  :config="config"
                                  :data="tableData"
                                  :count="tableData.length"
                                  ref="table"/>
        </div>
      </div>
    </div>
    <modal name="url"
           v-if="selectedClient"
           v-bind="urlModalProps">
      <UrlPopup :title="selectedUrl.label"
                :client="selectedClient._id"
                :url="selectedUrl._id"
                :date="snapshot_date"
                :selectedAdvancedOptions="selectedAdvancedOptions"
                @close="hidePopup" />
    </modal>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapState } from 'vuex'
import filters from '@/common/Filters'
import baseModalProps from '@/common/BaseModalProps'
import UrlPopup from '@/features/reports/common/UrlPopup'
import PieChart from '@/features/reports/common/PieChart'
import BarChart from '@/features/reports/common/BarChart'
import { convertAdvancedOptionsToFilters } from '@/features/reports/common/advanced-options/config'
import {
  baseComponent,
  topLevelTableConfig,
  secondLevelTableConfig,
  thirdLevelTableConfig,
  fourthLevelTableConfig
} from '@/features/reports/common/Mixins'
import { moveOtherToEnd } from '@/features/reports/common/Helper'

/**
 * Sections component. Used in RankZone
 */
export default {
  name: 'Sections',
  mixins: [baseComponent, topLevelTableConfig, secondLevelTableConfig, thirdLevelTableConfig, fourthLevelTableConfig],
  components: {
    PieChart,
    BarChart,
    UrlPopup
  },
  created () {
    this.prepareDataProperties()
  },
  props: {
    /**
     * Advanced options from parent element
     */
    selectedAdvancedOptions: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      // snapshot date selected from table header select
      snapshot_date: '',
      // url that should be shown in UrlPopup
      selectedUrl: {},
      // url that should be shown in UrlPopup
      formattedSnapshots: []
    }
  },
  computed: {
    ...mapState('rankZoneCommon', {
      /**
       * @alias expanedUrl
       * */
      expandedItem: 'expandedItem',
      /**
       * path to constant object holds paths to corresponding page specific store actions
       * to set received form BE keyword from common store into corresponding store
       * */
      KEYWORDS_PATH: 'KEYWORDS_PATH'
    }),
    ...mapState('rankZoneSection', {
      /**
       * prepare monthly_search_volume state data for {@link PieChart}
       * @param state
       * @returns {{footer: {name: string, value: number}, items: *}}
       */
      monthlySearchVolume (state) {
        let total = 0
        const sectionData = Object.keys(state.monthly_search_volume).map(name => {
          const msv = state.monthly_search_volume[name]
          total += msv
          return {
            name,
            value: msv
          }
        })

        return {
          footer: {
            name: 'Total MSV:',
            value: total
          },
          items: moveOtherToEnd(sectionData)
        }
      },
      isMonthlySearchVolume () {
        return this.monthlySearchVolume.items && this.monthlySearchVolume.items.length
      },
      /**
       * prepare share_of_voice data for {@link BarChart}
       * @param state
       */
      shareOfVoice (state) {
        return moveOtherToEnd(state.share_of_voice)
      },
      tableData (state) {
        return state.sections
      },
      /**
       * return 'unfiltered_totals' store array as master sum for table header
       */
      masterSum: 'unfiltered_totals'
    }),
    /**
     * modal props for {@link UrlPopup}
     * */
    urlModalProps: function () {
      const urlModalProps = {
        width: 980
      }
      return { ...baseModalProps, ...urlModalProps }
    },
    /**
     * Table config
     */
    config () {
      return [
        this.topLevelConfig,
        this.secondLevelConfig,
        this.thirdLevelConfig,
        this.forthLevelConfig
      ]
    }
  },
  watch: {
    selectedClient () {
      this.isDataFetched = false
    },
    selectedAdvancedOptions: {
      async handler () {
        this.isDataFetched && await this.applyAdvancedOptions()
      },
      deep: true
    },
    snapshots: {
      handler (snapshots) {
        if (!this.selectedClient || !this.snapshotsPresent) {
          return
        }
        this.mapSnapshots()
        this.$nextTick(this.loadPageData)
      },
      immediate: true
    },
    /**
     * watcher for {@link #expanedUrl} from store
     * starts loading keyword from backend
     * @param item
     */
    expandedItem (item) {
      if (item) {
        this.getKeywords(item._id)
      }
    }
  },
  methods: {
    ...mapActions('rankZoneSection', ['getSectionsData']),
    ...mapActions('rankZoneCommon', ['getKeywordsByUrlIdData']),
    async loadPageData () {
      const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
      const data = await this.getSectionsData({ clientId: this.clientId, filters: { advancedFilters } })
      data && (this.isDataFetched = true)
    },
    /**
     * snapshot dropdown callback
     */
    onSnapshotChanged: async function () {
      this.$refs['table'].collapseTable()
      await this.updateData()
    },
    /**
     * prepare snapshots for dropdown
     */
    mapSnapshots () {
      this.snapshot_date = ''
      this.snapshot_date = this.snapshots[0]
      this.formattedSnapshots = this.snapshots.map(snapshot => {
        return {
          label: this.$options.filters.formattedDate(snapshot),
          value: snapshot
        }
      })
    },
    /**
     * open {@link UrlPopup}
     */
    openPopup (data) {
      this.selectedUrl = {
        _id: data._id,
        label: data.label
      }
      this.$modal.show('url')
    },
     /**
     * hide {@link UrlPopup}
     */
    hidePopup () {
      this.$modal.hide('url')
      this.selectedUrl = {}
    },
    /**
     * invoked whether from {@link AdvancedOptions} component or from {@link advancedFiltersMixins}
     * @param options
     * @returns {Promise<void>}
     */
    async applyAdvancedOptions () {
      this.$refs['table'] && this.$refs['table'].collapseTable()
      await this.updateData()
    },
    /**
     * @alias updateDataCall
     * update data after applying advanced filters or changing snapshot
     */
    async updateData () {
      const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
      const snapshot_date = this.snapshot_date ? this.snapshot_date : null
      const filters = Object.assign({}, { snapshot_date, advancedFilters })
      if (this.isDataFetched) {
        this.isDataUpdating = true
      }
      await this.getSectionsData({ clientId: this.clientId, filters })
      this.isDataUpdating = false
      this.isDataFetched = true
    },
    getKeywords (urlId) {
      const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
      const snapshot_date = this.snapshot_date ? this.snapshot_date : null
      const filters = Object.assign({}, { snapshot_date, advancedFilters, url_id: urlId })
      this.getKeywordsByUrlIdData({ clientId: this.clientId, filters, screen: this.KEYWORDS_PATH.SECTION })
    },
    /**
     * prepare config for table
     */
    prepareDataProperties: function () {
      this.topLevelConfig.cells[0] = {
        ...this.topLevelConfig.cells[0],
        label: 'URL Section',
        subtitleSingle: 'section found',
        subtitlePlural: 'sections found'
      }
    }
  },
  filters: {
    ...filters
  }
}
</script>

<style scoped lang="scss">
  .section {
    margin-bottom: -50px;

    &__row {
      display: flex;
      align-items: stretch;
      justify-content: space-between;
      margin-bottom: 14px;

      &-item {
        flex: 1 1 100%;
        overflow: hidden;

        &:first-child {
          margin-right: 14px;
        }
      }
    }

    &__chart-wrapper {
      width: 100%;
      padding: 15px 0 30px 40px;
      box-sizing: border-box;
    }

    &__table {
      flex: 1 1;
    }

    &__table-controls {
      display: flex;
      align-items: center;
      justify-content: flex-end
    }

    &__select {
      flex: 0 0 160px;
    }

    &__advanced {
      margin: 14px 0;
    }
  }
</style>
